'use client';

import { useCallback } from 'react';

import { useTrackEvent } from '@/hooks/use-track-event';
import {
  CHAT_ANALYTICS_EVENTS,
  WIDGET_ANALYTICS_EVENTS,
  type AbortReason,
  type ChatAnalyticsEvent,
  type WidgetAnalyticsEvent,
} from '@/lib/constants/post-message-types';
import { useSyncStore } from '@/lib/sync/store';

export type LinkClickEventData = {
  url: string;
  linkText?: string;
  navigationMode: string;
  source:
    | 'markdown'
    | 'carousel'
    | 'flight-card'
    | 'vacation-package-card'
    | 'organized-trip-card'
    | 'sidebar';
  threadId?: string;
};

export type WidgetEventProperties = {
  trigger?: string;
};

export type CarouselShownEventData = {
  products: string[];
  toolName: string;
};

export type CardAction = 'view_rooms' | 'select_room';
export type CardItemType = 'hotel' | 'room';

export type CardActionEventData = {
  action: CardAction;
  itemType: CardItemType;
  /** The supplier's own id, so an action ties back to what was on screen. */
  itemId?: string;
  source?: LinkClickEventData['source'];
  /** Which surface rendered it, so one funnel can still tell them apart. */
  surface: 'chat' | 'widget';
  toolName?: string;
  /**
   * Where this action sits in a card flow that takes more than one, 1-based, and how many it takes
   * in total. Nothing is emitted for giving up, because nothing happens when a guest does: a funnel
   * over these reads abandonment as the step that has no successor, and needs to know which step of
   * how many to do it. Not room-specific — any in-card flow with steps can carry them.
   */
  step?: number;
  stepCount?: number;
};

export type ChatEventProperties = {
  threadId?: string;
  messageCount?: number;
  streamingDurationMs?: number;
  requestDurationMs?: number;
  abortReason?: AbortReason;
  isDocumentHidden?: boolean;
  isNetworkOffline?: boolean;
  isPagePersisted?: boolean;
  hadStreamingStarted?: boolean;
  wasQueued?: boolean;
  eventTimestamp?: number;
  queuedDurationMs?: number;
};

/**
 * How long a repeat of the same link counts as the same click. A guest who taps again because
 * nothing looked like it happened has reported one intent, not two — but coming back to the same
 * link later is a second visit, and that is worth counting.
 */
const LINK_CLICK_WINDOW_MS = 1000;

/**
 * Module scope, not a ref: the same link is rendered by more than one component (a card body and
 * its CTA, the same offer in a carousel and in the message above it), and a per-instance memory
 * would let each of them report the same burst.
 */
const lastLinkClickAt = new Map<string, number>();

/**
 * A wall clock can step backwards — an NTP correction, a user changing the time — and a timestamp
 * that is suddenly in the future must not swallow clicks until it catches up. Anything not inside
 * the window *behind* us is forgotten.
 */
function isWithinWindow({ at, now }: { at: number; now: number }): boolean {
  const elapsed = now - at;
  return elapsed >= 0 && elapsed <= LINK_CLICK_WINDOW_MS;
}

function isRepeatOfRecentClick({ key, now }: { key: string; now: number }): boolean {
  for (const [seen, at] of lastLinkClickAt) {
    if (!isWithinWindow({ at, now })) lastLinkClickAt.delete(seen);
  }
  const previous = lastLinkClickAt.get(key);
  lastLinkClickAt.set(key, now);
  return previous !== undefined && isWithinWindow({ at: previous, now });
}

export function useAnalytics() {
  const trackEvent = useTrackEvent();
  const activeThreadId = useSyncStore(state => state.threadId);

  const trackLinkClick = useCallback(
    (data: LinkClickEventData) => {
      const threadId = activeThreadId ?? data.threadId;
      // Every dimension the event is attributed by, so the memory can never be broader than the
      // reporting: which thread, which surface, which link, and which element of it — the text
      // tells a card-body click from its CTA, the source tells a carousel from the message above.
      const key = `${threadId ?? ''}|${data.source}|${data.url}|${data.linkText ?? ''}`;
      if (isRepeatOfRecentClick({ key, now: Date.now() })) return;

      trackEvent({
        event: CHAT_ANALYTICS_EVENTS.LINK_CLICKED,
        properties: { ...data, ...(threadId ? { threadId } : {}) },
      });
    },
    [trackEvent, activeThreadId],
  );

  const trackWidgetEvent = useCallback(
    (event: WidgetAnalyticsEvent, properties?: WidgetEventProperties) => {
      trackEvent({ event, properties });
    },
    [trackEvent],
  );

  const trackChatEvent = useCallback(
    (event: ChatAnalyticsEvent, properties?: ChatEventProperties) => {
      trackEvent({ event, properties });
    },
    [trackEvent],
  );

  const trackCardAction = useCallback(
    (data: CardActionEventData) => {
      trackEvent({
        event: CHAT_ANALYTICS_EVENTS.CARD_ACTION,
        properties: { ...data, ...(activeThreadId ? { threadId: activeThreadId } : {}) },
      });
    },
    [trackEvent, activeThreadId],
  );

  const trackCarouselShown = useCallback(
    (data: CarouselShownEventData) => {
      trackEvent({ event: CHAT_ANALYTICS_EVENTS.CAROUSEL_SHOWN, properties: data });
    },
    [trackEvent],
  );

  return {
    trackLinkClick,
    trackCardAction,
    trackWidgetEvent,
    trackChatEvent,
    trackCarouselShown,
    WIDGET_ANALYTICS_EVENTS,
    CHAT_ANALYTICS_EVENTS,
  };
}
